import type { ReactElement } from 'react';
import { Component } from 'react';
import * as _ from 'lodash';
import * as urlJoin from 'url-join';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CodeMirror from 'react-codemirror';
import Locator from '../../../shared/Locator';
import RevisionActions from '../../actions/RevisionActions';
import { window } from '../../globals';
import { errorMessageFromResponse } from '../../helpers/errors';
import { plural } from '../../../shared/helpers';
import { get } from '../../redux/api';
import type { AsyncStatus } from '../../types';
import { containEvent, nuc } from '../../utils';
import Flex from '../generic/Flex';
import Icon, { icons } from '../generic/Icon';
import Loader from '../generic/Loader';
import { Action, ResourceType } from '../../../typings/permissions';
import { renderRevision, renderPackage } from '../../../shared/helpers/renderLocator';
import type { NormalizedLicenseGroup, NormalizedMatch } from '../projects/issues/LicenseMatchList';
import CM from '../../vendor/codemirror';
import CliLicenseScannedMatches from '../licenses/CliLicenseScannedMatches';
import { WithDialogContext } from '../../contexts/DialogContext';
import type { DialogState } from '../../contexts/DialogContext';
import Text from '../generic/Text';
import { FILE_VIEWER_SIZE_LIMIT } from '../shared/revisions/FileViewer/FileContents';

const NEWLINE_CHAR_LN = 1;

type State = {
  fileContents?: any;
  fetchFileStatus: AsyncStatus;
  selected_paths: string[];
  private_project: boolean;
  mode?: any;
  rebuildingDependency?: boolean;
  searchQuery: string;
};

type Props = {
  // The modal is shown if and only if showingLicenseGroup is set.
  showingLicenseGroup: NormalizedLicenseGroup | null;
  currentPath: string | null;
  // revisionId is used to fetch file contents.
  revisionId: string;
  packageCacheError?: string;
  ignoreMatchesStatus: AsyncStatus;
  readOnly?: boolean;
  onHide(): void;
  onPathClick(path: string): void;
  onBulkIgnore(matches: NormalizedMatch[]): Promise<void>;
  cliLicenseScanWithMatchData: boolean;
  dialogContext: DialogState;
};

class ModalBrowseMatch extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      fileContents: null,
      fetchFileStatus: { loading: true },
      selected_paths: [],
      mode: null,
      private_project: false,
      rebuildingDependency: false,
      searchQuery: '',
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    const { showingLicenseGroup, currentPath } = this.props;
    const { selected_paths } = this.state;
    if (
      // Update the selected paths array when the modal is opened.
      (showingLicenseGroup && !prevProps.showingLicenseGroup) ||
      // The currentPath may have been reset after matches are ignored. If a selected path does not appear among the
      // matches, only currentPath will be selected.
      (showingLicenseGroup && currentPath && selected_paths.some(
        path => !showingLicenseGroup.matches.some(match => match.path === path),
      ))
    ) {
      this.setState({
        selected_paths: currentPath ? [currentPath] : [],
      });
    }
    if (currentPath && currentPath !== prevProps.currentPath) {
      this.onFetchFileContents();
    }
  }

  onIgnoreMatches = () => {
    const { showingLicenseGroup, dialogContext, onBulkIgnore } = this.props;
    if (!showingLicenseGroup || !this.state.selected_paths.length) {
      return;
    }

    dialogContext.onConfirm({
      title: 'Confirm license match ignore(s)',
      body: (
        <div>
          <p>
            You are about to ignore FOSSA for the following license path matches for
            {' '}
            <b>{showingLicenseGroup.licenseName}:</b>
          </p>
          {this.state.selected_paths.length > 10 ? (
            <div>
              <p>{this.state.selected_paths.slice(0, 10).join(', ')}</p>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip className="tooltip-wide">{this.state.selected_paths.slice(10).join(', ')}</Tooltip>}>
                <b style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                  {' '}
                  +{this.state.selected_paths.length - 10} more
                </b>
              </OverlayTrigger>
            </div>
          ) : (
            <div>{this.state.selected_paths.join(', ')}</div>
          )}
          <br />
          <p>
            <b>This action cannot be undone.</b> Are you sure you want to do this?
          </p>
        </div>
      ),
      onConfirm: () => onBulkIgnore(this.selectedMatches),
    });
  };

  onPathClick = (selectedPath: string) => {
    this.setState({
      selected_paths: [selectedPath],
    });
    this.props.onPathClick(selectedPath);
  };

  // checkbox click
  onSelectPath = path => {
    const selected_paths = this.state.selected_paths;
    const path_index = selected_paths.indexOf(path);
    if (path_index > -1) {
      selected_paths.splice(path_index, 1);
    } else {
      selected_paths.push(path);
    }

    this.setState({ selected_paths });
  };

  onSelectAll = () => {
    const { showingLicenseGroup } = this.props;
    if (showingLicenseGroup) {
      const { selectedMatches } = this;
      this.setState({
        selected_paths: selectedMatches.length === showingLicenseGroup.matches.length
          ? [] // Deselect all paths.
          : showingLicenseGroup.matches.map(match => match.path), // Select all paths.
      });
    }
  };

  onFetchFileContents = async () => {
    const { showingLicenseGroup, currentPath, revisionId } = this.props;
    if (!showingLicenseGroup || !currentPath) {
      return;
    }

    this.setState({
      fileContents: null,
      fetchFileStatus: { loading: true },
    });

    try {
      const { data, headers } = await get(
        urlJoin('/api/revisions', nuc(revisionId), 'blob', nuc(currentPath)),
        false,
        null,
        {
          responseType: (response) => response.ok ? 'text' : 'json',
        },
      );
      if (data === 'private_project') {
        // can't browse private projects
        this.setState({
          fetchFileStatus: { loading: false },
          private_project: true,
        });
        return;
      }

      const contentType = headers.get('content-type');
      this.setState({
        fileContents: data,
        fetchFileStatus: { loading: false },
        private_project: false,
        mode:
          CM.findModeByMIME(contentType) ||
          CM.findModeByExtension(_.last(currentPath.split('.'))),
      });

      setTimeout(() => {
        window.requestAnimationFrame(() => {
          const cm = this.getCodeMirror();
          let firstLine = null;
          if (!cm) {
            return;
          }
          showingLicenseGroup.matches.forEach(match => {
            if (currentPath === match.path) {
              _.each(match.sources || [], (src: string) => {
                // calculate start/end marker bounds
                try {
                  const new_src = src.split('-');
                  let l = -1;
                  let spos = parseInt(new_src[0]);
                  let epos = parseInt(new_src[1]);
                  let last_line;
                  let start;
                  let end;
                  while (epos > 0) {
                    l++;
                    const line = cm.getLine(l);
                    if (line == null) {
                      epos = -1;
                      l--;
                    } else {
                      last_line = line;
                      epos -= line.length + NEWLINE_CHAR_LN;
                      spos -= line.length + NEWLINE_CHAR_LN;
                      if (!start && spos <= 0) {
                        if (firstLine === null) {
                          firstLine = l;
                        }
                        start = {
                          line: l,
                          ch: line.length + spos + NEWLINE_CHAR_LN,
                        };
                      }
                    }
                  }
                  end = {
                    line: l,
                    ch: epos + last_line.length + NEWLINE_CHAR_LN,
                  };
                  cm.markText(start, end, {
                    className: 'cm-scan-match',
                    title: match.signature,
                    css: `background-color: ${this.getHighlightColor(match)}`,
                  });
                } catch (e) {

                }
              });
            }
          });
          cm.setCursor(firstLine || 0);
        });
      }, 1);
    } catch (err) {
      this.setState({
        fetchFileStatus: {
          loading: false,
          error: errorMessageFromResponse(err),
        },
      });
    }
  };

  getCodeMirror(): any {}

  getHighlightColor = match => {
    // permissiveness calc
    const contast_multiplier = 3;
    const redweight = 10; // how quickly it goes to red after passing oj (oj -> red only)
    const greenweight = 1; // how quickly it goes to green after passing oj (oj -> green only)
    if (match.RevisionLicense && match.RevisionLicense.license && match.RevisionLicense.license.permissiveness_score) {
      let score = match.RevisionLicense.license.permissiveness_score / 100;
      const base = 32; // min r and g values
      const max = 213; // max r and g values
      const d = max - base;
      score = (2 * d * score - d) * contast_multiplier;
      return (
        `rgba(${
          Math.round(max - (score > 0 ? score * greenweight : 0)) // red value, starts at max as decreases w/ more permissive (delta weighted by greenwight)
        },${
          Math.round(max + (score < 0 ? score * redweight : 0)) // green value, starts at max as decreases w/ less permissive (delta weighted by redweight)
        }, 70, ${
          Math.random() / 6 + 0.06
        })`
      ); // blue must be below 132
    }
    return 'rgba(130, 135, 184, .3)';
  };

  get selectedMatches(): NormalizedMatch[] {
    return _.intersectionBy<NormalizedMatch, string>(
      this.props.showingLicenseGroup?.matches || [],
      this.state.selected_paths,
      matchOrPath => _.isString(matchOrPath) ? matchOrPath : matchOrPath.path,
    );
  }

  // Filter path name
  filterMatches = (match: NormalizedMatch) => {
    // Empty search
    if (!this.state.searchQuery || this.state.searchQuery.length === 0) {
      return true;
    }

    return match.path.toUpperCase().includes(this.state.searchQuery.toUpperCase());
  };

  updateSearchQuery = (ev) => {
    this.setState({ searchQuery: ev.target.value });
  };

  // If the license matches come from a CLI License Scan, then we have downloaded a JSON blob with information about the
  // matches that should be displayed by the CliLicenseScannedMatches component.
  // If it is not CLI License Scanned, then we have downloaded the text of the file and should display it with CodeMirror.
  renderCode = () => {
    if (this.props.cliLicenseScanWithMatchData) {
      return (
        <CliLicenseScannedMatches
          licenseMatchJson={this.state.fileContents}
        />
      );
    }

    const fileSize = this.state.fileContents?.length ?? 0;
    if (fileSize > FILE_VIEWER_SIZE_LIMIT) {
      return (
        <div className="padded padded-small">
          <Text.Paragraph>
            This file is too large to be displayed.
          </Text.Paragraph>
        </div>
      );
    }

    return (
      <CodeMirror
        ref={cm => {
          if (!cm) {
            return;
          }
          this.getCodeMirror = cm.getCodeMirror;
        }}
        value={this.state.fileContents}
        options={{
          lineNumbers: true,
          readOnly: true,
          mode: this.state.mode ? this.state.mode.mode : null,
        }}
      />
    );
  };

  render() {
    const { showingLicenseGroup, packageCacheError, revisionId } = this.props;
    if (!showingLicenseGroup) {
      return null;
    }

    const { matches } = showingLicenseGroup;

    const navItems: ReactElement[] = matches.filter(this.filterMatches).map((match, i) => {
      const matchRow = (
        <a onClick={containEvent(this.onPathClick.bind(this, match.path))} key={i}>
          {!this.props.readOnly && (
            <span
              style={{ padding: '1px 14px 0 0' }}
              onClick={containEvent(this.onSelectPath.bind(null, match.path))}
            >
              {this.state.selected_paths.includes(match.path)
                ? (
                  <span className="item-checkbox-selected">
                    <Icon icon={icons.checkSquare} />
                  </span>
                  ) : (
                    <div className="item-checkbox" />
                  )}
            </span>
          )}
          <span className="path">
            {match.path}
          </span>
        </a>
      );

      return (
        <li className={match.path === this.props.currentPath ? 'active' : ''} key={match.path}>
          {match.path.length >= 25
            ? (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip className="tooltip-wide match-tooltip">{match.path}</Tooltip>}
              >
                {matchRow}
              </OverlayTrigger>
              ) : (
                matchRow
              )}
        </li>
      );
    });

    const currLoc = new Locator(revisionId).toParts();
    const projectNameAndVersion = `${renderPackage(currLoc)} (${renderRevision(currLoc)})`;
    const reloadStyle = { fontSize: '20px' };
    const fetchFileError = this.state.fetchFileStatus.error;

    return (
      <Modal
        onHide={this.props.onHide}
        animation
        show
        bsSize="large"
        className="modal-browse-file">
        <Modal.Header closeButton>
          <Modal.Title>
            <b>{showingLicenseGroup.licenseName}</b> License matches for {projectNameAndVersion}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="container-fluid">
          <Flex align="stretch">
            <div className="modal-browse-match-side-panel">
              {matches.length > 1 && (
                <input
                  type="text"
                  className="form-control input-search input-block-level"
                  placeholder="Search file paths"
                  onChange={this.updateSearchQuery}
                />
              )}
              <div className="browse-files-nav">
                {!this.props.readOnly && (
                  <div className="modal-browse-file-select-all">
                    <a onClick={containEvent(this.onSelectAll.bind(null))}>
                      {this.state.selected_paths.length === matches.length ? (
                        <span className="item-checkbox-selected">
                          <Icon icon={icons.checkSquare} />
                        </span>
                      ) : (
                        <div className="item-checkbox" />
                      )}
                    </a>
                    <b className="inline-header">
                      {this.state.selected_paths.length} / {matches.length} selected
                    </b>
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          Ignore{' '}
                          {this.state.selected_paths.length === matches.length ? (
                            'all'
                          ) : (
                            <span>{this.state.selected_paths.length}</span>
                          )}{' '}
                          {plural(this.state.selected_paths.length, 'match', 'matches')}
                        </Tooltip>
                      }>
                      <button
                        disabled={this.state.selected_paths.length < 1}
                        onClick={this.onIgnoreMatches}
                        className="pull-right btn btn-danger">
                        <i className="fa-solid fa-trash-can" />
                      </button>
                    </OverlayTrigger>
                  </div>
                )}
                <ul className="nav">{navItems}</ul>
              </div>
            </div>
            <div className="modal-browse-file-contents">
              {this.state.fetchFileStatus.loading || this.props.ignoreMatchesStatus.loading ? (
                <Loader />
              ) : (
                !packageCacheError && !fetchFileError ? (
                  this.state.private_project ? (
                    <div className="well fill nomargin flex-center">
                      Code browsing disabled for private projects. To view License match, please navigate to{' '}
                      <code>{this.props.currentPath}</code> in your project.
                    </div>
                  ) : !matches.length ? (
                    <div className="text-center">
                      <h2>No matches found</h2>
                      <p>This license was likely declared in package manifests or metadata.</p>
                    </div>
                  ) : this.renderCode()
                ) : (
                  <div className="well fill nomargin flex-center-column">
                    <Icon icon={icons.warning} />
                    {packageCacheError ? (
                      <div>
                        <b>There was an error caching this file:</b>
                        <p>{packageCacheError}</p>
                      </div>
                    ) : (
                      <p>File is unavailable for preview. {fetchFileError}</p>
                    )}
                    {window.hasPermission({
                      action: Action.Edit,
                      resourceType: ResourceType.ProjectAny,
                    }) ?
                      <Button
                        disabled={this.state.rebuildingDependency}
                        onClick={() => {
                          RevisionActions.rebuild(revisionId);
                          this.setState({ rebuildingDependency: true });
                        }}
                        style={{ marginTop: '10px' }}>
                        {this.state.rebuildingDependency
                          ? (
                            <span style={reloadStyle}>
                              <Icon icon={icons.circleO} hasSpin />
                            </span>
                            )
                          : (
                            <span style={reloadStyle}>
                              <Icon icon={icons.reload} />
                            </span>
                            )} Reanalyze dependency
                      </Button>
                      : <div>Ask an organization editor or admin to reanalyze this dependency.</div> }
                  </div>
                )
              )}
            </div>
          </Flex>
        </Modal.Body>
      </Modal>
    );
  }
}

export default WithDialogContext(ModalBrowseMatch);
